import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import swal from 'sweetalert';
import AuthContext, { User, SignInCredentials } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/authApi';
import eventEmitter from '../utils/eventEmitter';

interface AuthState {
  user: User | null;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();

  // Armazena estado do usuário somente em memória (sem localStorage)
  const [data, setData] = useState<AuthState>({ user: null });
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  /**
   * Faz logout (POST /auth/logout).
   */
  const signOut = useCallback(async () => {
    try {
      await authApi.post('/auth/logout/');
    } catch (err) {
      console.error('Erro ao fazer logout no servidor:', err);
    } finally {
      setData({ user: null });
      setAuthenticated(false);
      navigate('/login');
    }
  }, [navigate]);

  /**
   * Se usuário não for admin, força signOut + mensagem.
   */
  const handleNonAdmin = useCallback(() => {
    swal('Acesso Negado', 'Você não tem permissões administrativas.', 'error');
    signOut();
  }, [signOut]);

  /**
   * Verifica se user atual (cookie) está autenticado e é admin.
   */
  const checkAuth = useCallback(async () => {
    try {
      const response = await authApi.get('/users/me');
      const user = response.data as User;

      if (!user.is_system_admin) {
        handleNonAdmin();
        return;
      }

      setAuthenticated(true);
      setData({ user });
    } catch (error) {
      console.error('Erro na verificação de autenticação:', error);
      signOut();
    } finally {
      setLoading(false);
    }
  }, [handleNonAdmin, signOut]);

  // Primeira verificação ao montar
  useEffect(() => {
    checkAuth();
    // Se ocorrer "logout" por interceptors, ...
    const handleLogout = () => {
      signOut();
    };
    eventEmitter.on('logout', handleLogout);

    return () => {
      eventEmitter.off('logout', handleLogout);
    };
  }, [checkAuth, signOut]);

  /**
   * Função de login: POST /auth.
   */
  const signIn = useCallback(
    async ({ email, password }: SignInCredentials) => {
      try {
        const response = await authApi.post('/auth/', { email, password });
        const { user } = response.data as { user: User };

        if (!user.is_system_admin) {
          handleNonAdmin();
          return;
        }

        setData({ user });
        setAuthenticated(true);
        navigate('/dashboard');
      } catch (err: any) {
        setAuthenticated(false);
        if (err.response) {
          if (err.response.status === 401) {
            swal('Ooops! 401!', 'E-mail ou senha incorretos.', 'error');
          } else {
            swal(`Ooops! ${err.response.status}!`, 'Erro no login.', 'error');
          }
        } else {
          swal('Ooops! :(', `${err}`, 'error');
        }
      }
    },
    [handleNonAdmin, navigate]
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        authenticated,
        loading,
        signIn,
        signOut,
        // Implementar refreshUser se quiser recarregar /users/me
        refreshUserToken: () => {
          checkAuth();
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;